import { useEffect, useState, useMemo } from "react";
import { CalendarDays, Plus, ChevronLeft, ChevronRight, RefreshCw, RotateCcw, X, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DIAS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const DIAS_CURT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const TIPOS_COLOR = {
  avaliacao: "bg-[#EDE1F3] border-[#B084C4] text-[#7B569A]",
  sessao: "bg-[#DFF4F5] border-[#4EC0C6] text-[#2A7A80]",
};
const STATUS_COLOR = {
  agendada: "bg-slate-100 text-slate-700",
  confirmada: "bg-emerald-100 text-emerald-700",
  realizada: "bg-blue-100 text-blue-700",
  cancelada: "bg-slate-100 text-slate-500 line-through",
  faltou: "bg-rose-100 text-rose-700",
  reagendada: "bg-amber-100 text-amber-700 line-through",
};

function startOfWeek(d) {
  const dt = new Date(d);
  const day = dt.getDay(); // 0=Sunday
  const diff = (day === 0 ? -6 : 1 - day); // shift to Monday
  dt.setDate(dt.getDate() + diff);
  dt.setHours(0, 0, 0, 0);
  return dt;
}
function startOfMonth(d) { const dt = new Date(d.getFullYear(), d.getMonth(), 1); dt.setHours(0,0,0,0); return dt; }
function endOfMonth(d) { const dt = new Date(d.getFullYear(), d.getMonth() + 1, 0); dt.setHours(23,59,59,999); return dt; }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function isoLocal(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00`;
}
function dateOnly(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function fmtTime(iso) { return iso ? iso.slice(11, 16) : ""; }
function fmtDayShort(d) {
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}
function monthGridDays(monthDate) {
  // Returns 42 dates (6 weeks × 7) starting on Monday, ending on Sunday
  const first = startOfMonth(monthDate);
  const gridStart = startOfWeek(first);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

export default function Agenda() {
  const { user } = useAuth();
  const canWrite = user.role === "admin" || user.role === "recepcionista";

  const [monthStart, setMonthStart] = useState(startOfMonth(new Date()));
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [viewMode, setViewMode] = useState("semana"); // "semana" | "mes"
  const [selectedDay, setSelectedDay] = useState(null); // day panel
  const [sessoes, setSessoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [praticantes, setPraticantes] = useState([]);
  const [terapeutas, setTerapeutas] = useState([]);
  const [cavalos, setCavalos] = useState([]);

  const [dialogNew, setDialogNew] = useState(false);
  const [prefillDate, setPrefillDate] = useState(null);
  const [prefillTipo, setPrefillTipo] = useState("sessao");
  const [dialogDetail, setDialogDetail] = useState(null);
  const [dialogRecorr, setDialogRecorr] = useState(false);
  const [dialogReagendar, setDialogReagendar] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [dialogBulkDelete, setDialogBulkDelete] = useState(false);

  // Fetch a bit before and after the month to cover the visible grid (6 rows × 7 days)
  const gridDays = useMemo(() => monthGridDays(monthStart), [monthStart]);
  const gridStart = gridDays[0];
  const gridEnd = addDays(gridDays[41], 1);
  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);

  const fetchRange = async (inicio, fim) => {
    setLoading(true);
    try {
      const { data } = await api.get("/agenda/sessoes", {
        params: { inicio: dateOnly(inicio), fim: dateOnly(fim) },
      });
      setSessoes(data);
    } catch (e) { toast.error("Erro ao carregar agenda"); }
    finally { setLoading(false); }
  };

  const fetchMonth = () => fetchRange(gridStart, gridEnd);
  const fetchWeek = () => fetchRange(weekStart, weekEnd);
  const refetch = () => (viewMode === "semana" ? fetchWeek() : fetchMonth());

  useEffect(() => {
    if (viewMode === "semana") fetchWeek();
    else fetchMonth();
    // eslint-disable-next-line
  }, [monthStart, weekStart, viewMode]);

  useEffect(() => {
    Promise.all([
      api.get("/praticantes").then((r) => setPraticantes(r.data)).catch(() => {}),
      api.get("/funcionarios", { params: { cargo: "terapeuta" } }).then((r) => setTerapeutas(r.data)).catch(() => {}),
      api.get("/cavalos").then((r) => setCavalos(r.data)).catch(() => {}),
    ]);
  }, []);

  const sessoesByDay = useMemo(() => {
    const map = {};
    gridDays.forEach((d) => { map[dateOnly(d)] = []; });
    sessoes.forEach((s) => {
      const d = s.inicio?.slice(0, 10);
      if (map[d]) map[d].push(s);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.inicio.localeCompare(b.inicio)));
    return map;
  }, [sessoes, gridDays]);

  const openNewFor = (dayDate) => {
    if (!canWrite) return;
    setPrefillDate(dayDate);
    setPrefillTipo("avaliacao");
    setDialogNew(true);
  };

  return (
    <div data-testid="agenda-page">
      <PageHeader
        icon={CalendarDays}
        title="Agenda"
        description="Sessões e avaliações do mês. Clique em um dia para ver detalhes ou agendar."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refetch} data-testid="agenda-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Atualizar</Button>
            {canWrite && <Button size="sm" onClick={() => setDialogRecorr(true)} className="bg-brand-lilac hover:bg-[#9670B0] text-white" data-testid="agenda-recorr-btn"><RotateCcw className="h-4 w-4 mr-1" /> Nova recorrência</Button>}
            {canWrite && <Button size="sm" onClick={() => { setPrefillDate(new Date()); setPrefillTipo("avaliacao"); setDialogNew(true); }} className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white" data-testid="agenda-new-btn"><Plus className="h-4 w-4 mr-1" /> Nova avaliação</Button>}
            {canWrite && <Button size="sm" variant="outline" onClick={() => setDialogBulkDelete(true)} className="border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="agenda-bulkdel-btn"><Trash2 className="h-4 w-4 mr-1" /> Excluir em massa</Button>}
          </div>
        }
      />

      {/* View mode + navigation */}
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {viewMode === "semana" ? (
            <>
              <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="agenda-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="agenda-today">Hoje</Button>
              <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="agenda-next-week"><ChevronRight className="h-4 w-4" /></Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="icon" onClick={() => setMonthStart(addMonths(monthStart, -1))} data-testid="agenda-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => setMonthStart(startOfMonth(new Date()))} data-testid="agenda-today">Hoje</Button>
              <Button variant="outline" size="icon" onClick={() => setMonthStart(addMonths(monthStart, 1))} data-testid="agenda-next-month"><ChevronRight className="h-4 w-4" /></Button>
            </>
          )}
          <div className="ml-2 flex rounded-lg border border-border overflow-hidden text-xs">
            <button onClick={() => setViewMode("semana")} data-testid="agenda-view-semana"
              className={`px-3 py-1.5 font-semibold transition-colors ${viewMode === "semana" ? "bg-brand-turquoise text-white" : "text-slate-600 hover:bg-slate-50"}`}>Semana</button>
            <button onClick={() => setViewMode("mes")} data-testid="agenda-view-mes"
              className={`px-3 py-1.5 font-semibold border-l border-border transition-colors ${viewMode === "mes" ? "bg-brand-turquoise text-white" : "text-slate-600 hover:bg-slate-50"}`}>Mês</button>
          </div>
        </div>
        <p className="font-heading font-bold text-slate-800 text-lg capitalize" data-testid="agenda-month-label">
          {viewMode === "semana" ? (
            <>
              <span className="text-brand-turquoise">{weekStart.getDate()} {MESES[weekStart.getMonth()].slice(0, 3)}</span>
              {" — "}
              <span className="text-brand-turquoise">{addDays(weekStart, 6).getDate()} {MESES[addDays(weekStart, 6).getMonth()].slice(0, 3)} {addDays(weekStart, 6).getFullYear()}</span>
            </>
          ) : (
            <>{MESES[monthStart.getMonth()]} <span className="text-brand-turquoise">{monthStart.getFullYear()}</span></>
          )}
        </p>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#DFF4F5] border border-[#4EC0C6]" /> Sessão</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#EDE1F3] border border-[#B084C4]" /> Avaliação</span>
        </div>
      </div>

      {/* Grid: week view or month grid */}
      {loading ? (
        <div className="p-16 text-center text-muted-foreground">Carregando…</div>
      ) : viewMode === "semana" ? (
        <WeekView
          weekStart={weekStart}
          sessoes={sessoes}
          canWrite={canWrite}
          onOpenSessao={(s) => setDialogDetail(s)}
          onNewFor={(day, hour) => {
            if (!canWrite) return;
            const dt = new Date(day); dt.setHours(hour, 0, 0, 0);
            setPrefillDate(dt); setPrefillTipo("avaliacao"); setDialogNew(true);
          }}
        />
      ) : (
        <Card className="border-border shadow-none rounded-xl overflow-hidden bg-white">
          <div className="grid grid-cols-7 border-b border-border bg-slate-50">
            {DIAS_CURT.map((d) => (
              <div key={d} className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 text-center border-r border-border last:border-r-0">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 auto-rows-fr">
            {gridDays.map((dayDate, idx) => {
              const key = dateOnly(dayDate);
              const dayItems = sessoesByDay[key] || [];
              const isToday = dateOnly(new Date()) === key;
              const isCurrentMonth = dayDate.getMonth() === monthStart.getMonth();
              const isWeekend = idx % 7 >= 5;
              const isSelected = selectedDay && dateOnly(selectedDay) === key;
              const dayNum = dayDate.getDate();
              const MAX_VISIBLE = 3;
              const visible = dayItems.slice(0, MAX_VISIBLE);
              const extra = dayItems.length - visible.length;

              return (
                <div
                  key={key}
                  onClick={() => setSelectedDay(dayDate)}
                  onDoubleClick={() => canWrite && isCurrentMonth && openNewFor(dayDate)}
                  data-testid={`agenda-day-${key}`}
                  className={[
                    "min-h-[110px] border-r border-b border-border last:border-r-0 p-1.5 cursor-pointer transition-colors",
                    !isCurrentMonth ? "bg-slate-50/60 text-slate-400" : (isWeekend ? "bg-slate-50/30" : "bg-white"),
                    isSelected ? "ring-2 ring-inset ring-brand-turquoise" : "hover:bg-[#F0FBFC]/60",
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={[
                        "text-xs font-semibold rounded-full h-6 w-6 inline-flex items-center justify-center",
                        isToday ? "bg-brand-turquoise text-white" :
                        (isCurrentMonth ? "text-slate-700" : "text-slate-400"),
                      ].join(" ")}
                    >
                      {dayNum}
                    </span>
                    {canWrite && isCurrentMonth && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openNewFor(dayDate); }}
                        className="opacity-0 group-hover:opacity-100 h-5 w-5 rounded hover:bg-brand-turquoise/10 text-brand-turquoise flex items-center justify-center"
                        data-testid={`agenda-add-${key}`}
                        title="Novo agendamento"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {visible.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setDialogDetail(s); }}
                        data-testid={`agenda-sessao-${s.id}`}
                        className={`w-full text-left rounded px-1.5 py-0.5 text-[10px] border-l-2 ${TIPOS_COLOR[s.tipo] || TIPOS_COLOR.sessao} truncate leading-tight hover:brightness-95`}
                      >
                        <span className="font-semibold">{fmtTime(s.inicio)}</span>{" "}
                        <span className="truncate">{s.praticante_nome || "Praticante"}</span>
                      </button>
                    ))}
                    {extra > 0 && (
                      <p className="text-[10px] text-brand-turquoise font-medium pl-1">+{extra} mais</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Day details panel (only in mês view) */}
      {viewMode === "mes" && selectedDay && (
        <DayDetailPanel
          day={selectedDay}
          items={sessoesByDay[dateOnly(selectedDay)] || []}
          onClose={() => setSelectedDay(null)}
          onOpen={(s) => setDialogDetail(s)}
          onNew={() => canWrite && openNewFor(selectedDay)}
          canWrite={canWrite}
        />
      )}

      {dialogNew && (
        <SessaoDialog
          open={dialogNew} onClose={() => setDialogNew(false)}
          prefillDate={prefillDate}
          prefillTipo={prefillTipo}
          praticantes={praticantes} terapeutas={terapeutas} cavalos={cavalos}
          onCreated={() => { setDialogNew(false); setPrefillTipo("avaliacao"); refetch(); }}
        />
      )}

      {dialogDetail && (
        <SessaoDetail
          sessao={dialogDetail} open={!!dialogDetail} onClose={() => setDialogDetail(null)}
          praticantes={praticantes} terapeutas={terapeutas} cavalos={cavalos}
          canWrite={canWrite}
          onUpdated={() => { setDialogDetail(null); refetch(); }}
          onDelete={(s) => { setDialogDetail(null); setConfirmDelete(s); }}
          onReagendar={(s) => { setDialogDetail(null); setDialogReagendar(s); }}
        />
      )}

      {dialogReagendar && (
        <ReagendarDialog
          sessao={dialogReagendar}
          onClose={() => setDialogReagendar(null)}
          terapeutas={terapeutas} cavalos={cavalos}
          onDone={() => { setDialogReagendar(null); refetch(); }}
        />
      )}

      {dialogRecorr && (
        <RecorrenciaDialog
          open={dialogRecorr} onClose={() => setDialogRecorr(false)}
          praticantes={praticantes} terapeutas={terapeutas} cavalos={cavalos}
          onDone={() => { setDialogRecorr(false); refetch(); }}
        />
      )}

      {dialogBulkDelete && (
        <BulkDeleteDialog
          onClose={() => setDialogBulkDelete(false)}
          praticantes={praticantes}
          onDone={() => { setDialogBulkDelete(false); refetch(); }}
        />
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir sessão?</AlertDialogTitle><AlertDialogDescription>A sessão será removida da agenda.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="agenda-delete-confirm"
              onClick={async () => {
                try {
                  await api.delete(`/agenda/sessoes/${confirmDelete.id}`);
                  toast.success("Sessão excluída");
                  refetch();
                } catch (e) { toast.error("Erro"); }
                finally { setConfirmDelete(null); }
              }}
              className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SessaoDialog({ open, onClose, prefillDate, prefillTipo, praticantes, terapeutas, cavalos, onCreated }) {
  const initialHora = prefillDate && (prefillDate.getHours() !== 0 || prefillDate.getMinutes() !== 0)
    ? `${String(prefillDate.getHours()).padStart(2, "0")}:${String(prefillDate.getMinutes()).padStart(2, "0")}`
    : "09:00";
  const [form, setForm] = useState({
    praticante_id: "", terapeuta_id: "", cavalo_id: "",
    data: prefillDate ? dateOnly(prefillDate) : dateOnly(new Date()),
    hora: initialHora, duracao_min: "60", tipo: prefillTipo || "sessao", observacoes: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.praticante_id) return toast.error("Selecione o praticante");
    setSaving(true);
    try {
      const [h, m] = form.hora.split(":").map(Number);
      const inicio = new Date(`${form.data}T00:00:00`);
      inicio.setHours(h, m, 0, 0);
      const fim = new Date(inicio.getTime() + parseInt(form.duracao_min, 10) * 60000);
      const payload = {
        praticante_id: form.praticante_id,
        terapeuta_id: form.terapeuta_id || undefined,
        cavalo_id: form.cavalo_id || undefined,
        inicio: isoLocal(inicio),
        fim: isoLocal(fim),
        tipo: form.tipo,
        observacoes: form.observacoes || undefined,
      };
      await api.post("/agenda/sessoes", payload);
      toast.success("Sessão agendada");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{form.tipo === "avaliacao" ? "Nova avaliação" : "Nova sessão"}</DialogTitle>
          <DialogDescription>Até 3 praticantes por horário.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <F label="Praticante" req>
            <Select value={form.praticante_id} onValueChange={(v) => setForm({ ...form, praticante_id: v })}>
              <SelectTrigger data-testid="sessao-form-praticante"><SelectValue placeholder="Selecionar" /></SelectTrigger>
              <SelectContent>{praticantes.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <div className="grid grid-cols-3 gap-3">
            <F label="Data"><Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="sessao-form-data" /></F>
            <F label="Hora"><Input type="time" value={form.hora} onChange={(e) => setForm({ ...form, hora: e.target.value })} data-testid="sessao-form-hora" /></F>
            <F label="Duração (min)"><Input type="number" step="15" value={form.duracao_min} onChange={(e) => setForm({ ...form, duracao_min: e.target.value })} /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Tipo">
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="sessao-form-tipo"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sessao">Sessão</SelectItem><SelectItem value="avaliacao">Avaliação</SelectItem></SelectContent>
              </Select>
            </F>
            <F label="Terapeuta">
              <Select value={form.terapeuta_id} onValueChange={(v) => setForm({ ...form, terapeuta_id: v })}>
                <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                <SelectContent>{terapeutas.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>
          <F label="Cavalo">
            <Select value={form.cavalo_id} onValueChange={(v) => setForm({ ...form, cavalo_id: v })}>
              <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
              <SelectContent>{cavalos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Observações"><Textarea rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-brand-turquoise hover:bg-[#3A9CA2]" data-testid="sessao-form-submit">{saving ? "Salvando…" : "Agendar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SessaoDetail({ sessao, open, onClose, canWrite, onUpdated, onDelete, onReagendar }) {
  const [status, setStatus] = useState(sessao.status);
  const [saving, setSaving] = useState(false);
  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      await api.patch(`/agenda/sessoes/${sessao.id}`, { status: newStatus });
      setStatus(newStatus);
      toast.success("Status atualizado");
      onUpdated();
    } catch (e) { toast.error("Erro"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sessao-detail">
        <DialogHeader>
          <DialogTitle>{sessao.praticante_nome || "Sessão"}</DialogTitle>
          <DialogDescription>
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {sessao.inicio.slice(0, 10)} · {fmtTime(sessao.inicio)}–{fmtTime(sessao.fim)}</span>
            <span className="mx-2">·</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[status]}`}>{status}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2"><Badge variant="secondary">{sessao.tipo}</Badge></div>
          {sessao.observacoes && <p className="text-slate-700">{sessao.observacoes}</p>}
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2 mt-4">
            <Button size="sm" variant="outline" disabled={saving || status === "agendada"} onClick={() => updateStatus("agendada")}>Agendada</Button>
            <Button size="sm" variant="outline" disabled={saving} onClick={() => updateStatus("confirmada")}>Confirmar</Button>
            <Button size="sm" variant="outline" disabled={saving} onClick={() => updateStatus("realizada")}>Realizada</Button>
            <Button size="sm" variant="outline" disabled={saving} onClick={() => updateStatus("faltou")}>Faltou</Button>
            <Button size="sm" variant="outline" disabled={saving} onClick={() => updateStatus("cancelada")}>Cancelar</Button>
            <Button size="sm" variant="outline" disabled={saving} onClick={() => updateStatus("reagendada")} className="border-amber-300 text-amber-700 hover:bg-amber-50">Marcar reagendada</Button>
            <Button size="sm" onClick={() => onReagendar(sessao)} className="bg-brand-lilac hover:bg-[#9670B0]" data-testid="sessao-reagendar-btn">Reagendar (nova data)</Button>
            <Button size="sm" variant="destructive" onClick={() => onDelete(sessao)}>Excluir</Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReagendarDialog({ sessao, onClose, terapeutas, cavalos, onDone }) {
  const [form, setForm] = useState({
    motivo: "falta", motivo_detalhe: "",
    data: sessao.inicio.slice(0, 10),
    hora: sessao.inicio.slice(11, 16),
    duracao_min: 60,
  });
  const [saving, setSaving] = useState(false);
  const doReagendar = async () => {
    setSaving(true);
    try {
      const [h, m] = form.hora.split(":").map(Number);
      const ini = new Date(`${form.data}T00:00:00`); ini.setHours(h, m);
      const fim = new Date(ini.getTime() + parseInt(form.duracao_min, 10) * 60000);
      await api.post(`/agenda/sessoes/${sessao.id}/reagendar`, {
        motivo: form.motivo,
        motivo_detalhe: form.motivo_detalhe || undefined,
        novo_inicio: isoLocal(ini), novo_fim: isoLocal(fim),
      });
      toast.success("Sessão reagendada");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Reagendar sessão</DialogTitle><DialogDescription>Uma nova sessão será criada e a original marcada como reagendada.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <F label="Motivo" req>
            <Select value={form.motivo} onValueChange={(v) => setForm({ ...form, motivo: v })}>
              <SelectTrigger data-testid="reagendar-motivo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="falta">Falta</SelectItem>
                <SelectItem value="cancelamento">Cancelamento</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Detalhe"><Input value={form.motivo_detalhe} onChange={(e) => setForm({ ...form, motivo_detalhe: e.target.value })} /></F>
          <div className="grid grid-cols-3 gap-3">
            <F label="Nova data"><Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="reagendar-data" /></F>
            <F label="Nova hora"><Input type="time" value={form.hora} onChange={(e) => setForm({ ...form, hora: e.target.value })} /></F>
            <F label="Duração (min)"><Input type="number" step="15" value={form.duracao_min} onChange={(e) => setForm({ ...form, duracao_min: e.target.value })} /></F>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={doReagendar} disabled={saving} className="bg-brand-lilac hover:bg-[#9670B0]" data-testid="reagendar-submit">{saving ? "Reagendando…" : "Reagendar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecorrenciaDialog({ open, onClose, praticantes, terapeutas, cavalos, onDone }) {
  const [form, setForm] = useState({
    praticante_id: "", terapeuta_id: "", cavalo_id: "",
    data_inicio: dateOnly(new Date()), meses: 12, tipo: "sessao",
    horarios: [{ _k: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `h-${Date.now()}`, dia_semana: 1, hora: "09:00", duracao_min: 60 }],
  });
  const [saving, setSaving] = useState(false);

  const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `h-${Date.now()}-${Math.random()}`;
  const addHorario = () => setForm({ ...form, horarios: [...form.horarios, { _k: newKey(), dia_semana: 3, hora: "14:00", duracao_min: 60 }] });
  const rmHorario = (i) => setForm({ ...form, horarios: form.horarios.filter((_, idx) => idx !== i) });
  const setH = (i, k, v) => {
    const cp = [...form.horarios]; cp[i] = { ...cp[i], [k]: v }; setForm({ ...form, horarios: cp });
  };

  const doCreate = async () => {
    if (!form.praticante_id) return toast.error("Selecione o praticante");
    if (!form.horarios.length) return toast.error("Adicione ao menos um horário");
    setSaving(true);
    try {
      const payload = {
        praticante_id: form.praticante_id,
        terapeuta_id: form.terapeuta_id || undefined,
        cavalo_id: form.cavalo_id || undefined,
        horarios: form.horarios.map((h) => ({ dia_semana: parseInt(h.dia_semana, 10), hora: h.hora, duracao_min: parseInt(h.duracao_min, 10) })),
        data_inicio: form.data_inicio,
        meses: parseInt(form.meses, 10),
        tipo: form.tipo,
      };
      const { data } = await api.post("/agenda/recorrencia", payload);
      toast.success(`${data.sessoes_criadas} sessões criadas${data.sessoes_ignoradas_slot_cheio ? ` (${data.sessoes_ignoradas_slot_cheio} ignoradas por slot cheio)` : ""}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nova recorrência</DialogTitle><DialogDescription>Gera sessões automáticas por N meses nos dias/horas informados.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Praticante" req>
              <Select value={form.praticante_id} onValueChange={(v) => setForm({ ...form, praticante_id: v })}>
                <SelectTrigger data-testid="recorr-praticante"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>{praticantes.filter((p) => p.tipo === "ativo").map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Terapeuta">
              <Select value={form.terapeuta_id} onValueChange={(v) => setForm({ ...form, terapeuta_id: v })}>
                <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                <SelectContent>{terapeutas.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Cavalo">
              <Select value={form.cavalo_id} onValueChange={(v) => setForm({ ...form, cavalo_id: v })}>
                <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                <SelectContent>{cavalos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Tipo">
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sessao">Sessão</SelectItem><SelectItem value="avaliacao">Avaliação</SelectItem></SelectContent>
              </Select>
            </F>
            <F label="Data de início" req><Input type="date" value={form.data_inicio} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} data-testid="recorr-inicio" /></F>
            <F label="Meses"><Input type="number" min="1" max="24" value={form.meses} onChange={(e) => setForm({ ...form, meses: e.target.value })} data-testid="recorr-meses" /></F>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Horários fixos por semana</Label>
              <Button size="sm" variant="outline" onClick={addHorario}><Plus className="h-3 w-3 mr-1" /> Adicionar</Button>
            </div>
            <div className="space-y-2">
              {form.horarios.map((h, i) => (
                <div key={h._k || i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    <Label className="text-[10px] uppercase text-muted-foreground">Dia da semana</Label>
                    <Select value={String(h.dia_semana)} onValueChange={(v) => setH(i, "dia_semana", v)}>
                      <SelectTrigger data-testid={`recorr-dia-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DIAS.map((d, idx) => <SelectItem key={d} value={String(idx)}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px] uppercase text-muted-foreground">Hora</Label>
                    <Input type="time" value={h.hora} onChange={(e) => setH(i, "hora", e.target.value)} data-testid={`recorr-hora-${i}`} />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px] uppercase text-muted-foreground">Duração</Label>
                    <Input type="number" step="15" value={h.duracao_min} onChange={(e) => setH(i, "duracao_min", e.target.value)} />
                  </div>
                  <div className="col-span-1">
                    {form.horarios.length > 1 && <Button size="icon" variant="ghost" onClick={() => rmHorario(i)}><X className="h-4 w-4" /></Button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={doCreate} disabled={saving} className="bg-brand-lilac hover:bg-[#9670B0]" data-testid="recorr-submit">{saving ? "Gerando…" : "Gerar recorrência"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, req, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}{req && <span className="text-destructive"> *</span>}</Label>{children}</div>);
}

function DayDetailPanel({ day, items, onClose, onOpen, onNew, canWrite }) {
  const label = day.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white border-l border-border shadow-2xl z-40 flex flex-col" data-testid="agenda-day-panel">
      <div className="px-5 py-4 border-b border-border flex items-start justify-between bg-gradient-to-r from-[#F0FBFC] to-white">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-brand-turquoise font-bold">Detalhes do dia</p>
          <h3 className="font-heading font-bold text-base capitalize text-slate-800">{label}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{items.length} agendamento{items.length !== 1 ? "s" : ""}</p>
        </div>
        <Button size="icon" variant="ghost" onClick={onClose} data-testid="agenda-day-panel-close"><X className="h-4 w-4" /></Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {items.length === 0 ? (
          <div className="text-center py-16 text-sm text-muted-foreground">
            <p>Nenhum agendamento neste dia.</p>
            {canWrite && <Button size="sm" onClick={onNew} className="mt-3 bg-brand-turquoise hover:bg-[#3A9CA2] text-white"><Plus className="h-3 w-3 mr-1" /> Agendar</Button>}
          </div>
        ) : items.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onOpen(s)}
            data-testid={`agenda-day-item-${s.id}`}
            className={`w-full text-left rounded-lg px-3 py-2 border-l-4 hover:shadow-sm transition-shadow ${s.tipo === "avaliacao" ? "bg-[#EDE1F3]/40 border-[#B084C4]" : "bg-[#DFF4F5]/40 border-[#4EC0C6]"}`}
          >
            <div className="flex items-center justify-between mb-0.5">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700"><Clock className="h-3 w-3" /> {s.inicio?.slice(11, 16)}–{s.fim?.slice(11, 16)}</span>
              <Badge variant="secondary" className="text-[9px] uppercase">{s.tipo === "avaliacao" ? "Avaliação" : "Sessão"}</Badge>
            </div>
            <p className="font-medium text-sm text-slate-800 truncate">{s.praticante_nome || "Praticante"}</p>
            {s.terapeuta_nome && <p className="text-[11px] text-muted-foreground truncate">com {s.terapeuta_nome}</p>}
          </button>
        ))}
      </div>
      {canWrite && items.length > 0 && (
        <div className="p-3 border-t border-border">
          <Button onClick={onNew} className="w-full bg-brand-turquoise hover:bg-[#3A9CA2] text-white" data-testid="agenda-day-panel-add">
            <Plus className="h-4 w-4 mr-1" /> Adicionar neste dia
          </Button>
        </div>
      )}
    </div>
  );
}



function BulkDeleteDialog({ onClose, praticantes, onDone }) {
  const [praticanteId, setPraticanteId] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessoes, setSessoes] = useState([]);            // sessões futuras do praticante
  const [selected, setSelected] = useState(new Set());   // ids marcados
  const [confirmando, setConfirmando] = useState(false);

  // Ao selecionar o praticante, carrega as próximas sessões (agendadas/confirmadas, futuras)
  useEffect(() => {
    if (!praticanteId) { setSessoes([]); setSelected(new Set()); return; }
    let alive = true;
    setLoading(true);
    const hoje = dateOnly(new Date());
    const fim = new Date(); fim.setFullYear(fim.getFullYear() + 2);
    api.get("/agenda/sessoes", { params: { inicio: hoje, fim: dateOnly(fim), praticante_id: praticanteId } })
      .then(({ data }) => {
        if (!alive) return;
        const futuras = (data || [])
          .filter((s) => ["agendada", "confirmada"].includes(s.status))
          .sort((a, b) => (a.inicio || "").localeCompare(b.inicio || ""));
        setSessoes(futuras);
        setSelected(new Set());
      })
      .catch(() => toast.error("Erro ao carregar sessões"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [praticanteId]);

  const toggleOne = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const selectAll = () => setSelected(new Set(sessoes.map((s) => s.id)));
  const clearAll = () => setSelected(new Set());
  const selectFirstN = (n) => setSelected(new Set(sessoes.slice(0, n).map((s) => s.id)));
  const selectMonth = (offset) => {
    // offset=0 -> mês atual; offset=1 -> próximo mês; etc.
    const base = new Date();
    const ini = new Date(base.getFullYear(), base.getMonth() + offset, 1);
    const fim = new Date(base.getFullYear(), base.getMonth() + offset + 1, 1);
    const ids = sessoes
      .filter((s) => {
        const d = new Date(s.inicio);
        return d >= ini && d < fim;
      })
      .map((s) => s.id);
    setSelected(new Set(ids));
  };
  const selectNextMonths = (months) => {
    const base = new Date(); base.setHours(0, 0, 0, 0);
    const fim = new Date(base.getFullYear(), base.getMonth() + months, base.getDate());
    const ids = sessoes.filter((s) => {
      const d = new Date(s.inicio);
      return d >= base && d < fim;
    }).map((s) => s.id);
    setSelected(new Set(ids));
  };

  const submit = async () => {
    if (!praticanteId) return toast.error("Selecione o praticante");
    if (selected.size === 0) return toast.error("Selecione ao menos uma sessão");
    setConfirmando(true);
    try {
      const { data } = await api.post("/agenda/sessoes/bulk-delete", {
        ids: Array.from(selected),
      });
      toast.success(`${data.deleted} sessão(ões) excluída(s) — foram para a Lixeira`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir");
    } finally { setConfirmando(false); }
  };

  const fmtLine = (s) => {
    const d = new Date(s.inicio);
    const data = d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
    const hora = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    return `${data} · ${hora}`;
  };

  const praticantesOrdenados = [...praticantes].sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" data-testid="agenda-bulkdel-dialog">
        <DialogHeader>
          <DialogTitle>Excluir agendamentos de um praticante</DialogTitle>
          <DialogDescription>
            Selecione o praticante, escolha um atalho (mês, próximas N) ou marque manualmente as sessões.
            Só aparecem sessões futuras <b>agendadas</b> ou <b>confirmadas</b>. Todas as excluídas vão para a Lixeira.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Praticante</Label>
            <Select value={praticanteId} onValueChange={setPraticanteId}>
              <SelectTrigger data-testid="bulkdel-praticante"><SelectValue placeholder="Selecionar praticante" /></SelectTrigger>
              <SelectContent>{praticantesOrdenados.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {praticanteId && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-2 block">Atalhos rápidos</Label>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => selectFirstN(1)} className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-slate-50" data-testid="bulkdel-quick-1">Próxima sessão</button>
                  <button type="button" onClick={() => selectFirstN(3)} className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-slate-50" data-testid="bulkdel-quick-3">Próximas 3</button>
                  <button type="button" onClick={() => selectFirstN(5)} className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-slate-50" data-testid="bulkdel-quick-5">Próximas 5</button>
                  <button type="button" onClick={() => selectMonth(0)} className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-slate-50" data-testid="bulkdel-quick-mes-atual">Mês atual</button>
                  <button type="button" onClick={() => selectMonth(1)} className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-slate-50" data-testid="bulkdel-quick-prox-mes">Próximo mês</button>
                  <button type="button" onClick={() => selectNextMonths(3)} className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-slate-50" data-testid="bulkdel-quick-3m">Próximos 3 meses</button>
                  <button type="button" onClick={selectAll} className="px-3 py-1.5 rounded-lg border border-brand-turquoise/50 text-xs font-medium text-brand-turquoise hover:bg-brand-turquoise/5" data-testid="bulkdel-select-all">Selecionar tudo</button>
                  <button type="button" onClick={clearAll} className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-slate-500 hover:bg-slate-50">Limpar</button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto border border-border rounded-lg">
                {loading ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">Carregando sessões…</div>
                ) : sessoes.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    Nenhuma sessão futura agendada para este praticante.
                  </div>
                ) : (
                  <ul className="divide-y divide-border">
                    {sessoes.map((s) => {
                      const marcado = selected.has(s.id);
                      return (
                        <li key={s.id}
                            onClick={() => toggleOne(s.id)}
                            data-testid={`bulkdel-sessao-${s.id}`}
                            className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${marcado ? "bg-rose-50" : "hover:bg-slate-50"}`}>
                          <input type="checkbox" checked={marcado} onChange={() => toggleOne(s.id)} className="h-4 w-4" />
                          <div className="flex-1 flex items-center gap-3 text-sm">
                            <span className="font-medium text-slate-800">{fmtLine(s)}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${TIPOS_COLOR[s.tipo] || "bg-slate-100"}`}>{s.tipo}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLOR[s.status] || ""}`}>{s.status}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className={`rounded-lg p-3 text-sm border ${selected.size > 0 ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-slate-50 border-border text-slate-600"}`}>
                <b>{selected.size}</b> sessão(ões) selecionada(s) de <b>{sessoes.length}</b>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={confirmando || selected.size === 0}
                  className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="bulkdel-submit">
            <Trash2 className="h-4 w-4 mr-1" /> {confirmando ? "Excluindo…" : `Excluir ${selected.size || ""} sessão(ões)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// WeekView — grade semanal com dias em colunas, horários na lateral e linha
// horizontal live acompanhando o horário atual.
// ---------------------------------------------------------------------------
const HOUR_START = 7;
const HOUR_END = 21;   // exclusivo — vai de 7:00 até 20:59
const HOUR_H = 56;     // altura em px de cada hora

function WeekView({ weekStart, sessoes, canWrite, onOpenSessao, onNewFor }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const hours = useMemo(() => Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i), []);

  const sessoesByDay = useMemo(() => {
    const map = {};
    days.forEach((d) => { map[dateOnly(d)] = []; });
    sessoes.forEach((s) => {
      const key = s.inicio?.slice(0, 10);
      if (map[key]) map[key].push(s);
    });
    return map;
  }, [sessoes, days]);

  const nowKey = dateOnly(now);
  const todayInWeek = days.some((d) => dateOnly(d) === nowKey);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const rangeStartMin = HOUR_START * 60;
  const rangeEndMin = HOUR_END * 60;
  const nowTop = ((nowMinutes - rangeStartMin) / 60) * HOUR_H;
  const nowVisible = todayInWeek && nowMinutes >= rangeStartMin && nowMinutes < rangeEndMin;

  return (
    <Card className="border-border shadow-none rounded-xl overflow-hidden bg-white" data-testid="agenda-week-view">
      {/* Header: coluna vazia (labels de hora) + 7 dias */}
      <div className="grid border-b border-border bg-slate-50" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        <div />
        {days.map((d, i) => {
          const isToday = dateOnly(d) === nowKey;
          return (
            <div key={dateOnly(d)} className={`px-2 py-2 text-center border-r border-border last:border-r-0 ${isToday ? "bg-brand-turquoise/10" : ""}`}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{DIAS_CURT[i]}</p>
              <p className={`text-lg font-heading font-bold ${isToday ? "text-brand-turquoise" : "text-slate-700"}`}>{d.getDate()}</p>
            </div>
          );
        })}
      </div>

      {/* Body: grade de horas × dias, com linha do horário atual e sessões absolutas */}
      <div className="relative">
        <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
          {/* Coluna de horas */}
          <div>
            {hours.map((h) => (
              <div key={h} style={{ height: HOUR_H }} className="border-b border-r border-border text-[10px] text-right pr-1 pt-0.5 text-slate-500 font-medium">
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>
          {/* 7 colunas de dias */}
          {days.map((d) => {
            const key = dateOnly(d);
            const dayItems = sessoesByDay[key] || [];
            const isToday = key === nowKey;
            return (
              <div key={key} className={`relative border-r border-border last:border-r-0 ${isToday ? "bg-brand-turquoise/[.03]" : ""}`}>
                {/* Cliques nos slots vazios */}
                {hours.map((h) => (
                  <div
                    key={h}
                    style={{ height: HOUR_H }}
                    className="border-b border-border hover:bg-brand-turquoise/5 cursor-pointer transition-colors"
                    onClick={() => canWrite && onNewFor(d, h)}
                    data-testid={`week-slot-${key}-${h}`}
                  />
                ))}
                {/* Sessões posicionadas absolutamente */}
                {dayItems.map((s) => {
                  const hh = parseInt(s.inicio.slice(11, 13), 10);
                  const mm = parseInt(s.inicio.slice(14, 16), 10);
                  const startMinAbs = hh * 60 + mm;
                  if (startMinAbs < rangeStartMin || startMinAbs >= rangeEndMin) return null;
                  const top = ((startMinAbs - rangeStartMin) / 60) * HOUR_H;
                  // Duração calculada preferencialmente do intervalo inicio→fim; fallback duracao_min ou 60min.
                  let dur = s.duracao_min;
                  if (!dur && s.fim) {
                    try {
                      dur = Math.max(15, Math.round((new Date(s.fim) - new Date(s.inicio)) / 60000));
                    } catch { dur = 60; }
                  }
                  dur = dur || 60;
                  const height = Math.max(24, (dur / 60) * HOUR_H - 2);
                  const status = s.status || "agendada";
                  const dim = ["cancelada", "reagendada"].includes(status);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpenSessao(s); }}
                      data-testid={`week-sessao-${s.id}`}
                      style={{ top, height, position: "absolute", left: 2, right: 2 }}
                      className={`text-left rounded-md px-1.5 py-1 text-[10px] border-l-2 overflow-hidden hover:brightness-95 hover:shadow-sm transition ${TIPOS_COLOR[s.tipo] || TIPOS_COLOR.sessao} ${dim ? "opacity-50 line-through" : ""}`}
                    >
                      <p className="font-semibold leading-tight">{fmtTime(s.inicio)}</p>
                      <p className="truncate leading-tight">{s.praticante_nome || "Praticante"}</p>
                      {height > 40 && s.terapeuta_nome && (
                        <p className="truncate leading-tight text-[9px] opacity-80">{s.terapeuta_nome}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Linha "AGORA" — cruza toda a área da grade */}
        {nowVisible && (
          <div
            style={{ top: nowTop, left: 56 }}
            className="absolute right-0 z-20 pointer-events-none"
            data-testid="week-now-line"
          >
            <div className="relative">
              <div className="h-0.5 bg-rose-500 shadow-sm" />
              <div className="absolute left-0 -top-[5px] h-3 w-3 rounded-full bg-rose-500 ring-2 ring-white" />
              <div className="absolute -left-14 -top-2 text-[10px] font-semibold text-rose-600 bg-white/90 px-1 rounded">
                {String(now.getHours()).padStart(2, "0")}:{String(now.getMinutes()).padStart(2, "0")}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

